import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import Main from '../../../component/Main';
import Loading from '../../../component/common/loading/Loading';
import NewCommonSelect from '../../../component/common/selectBox/NewCommonSelect';
import generateFidelityMngList from './generate_fidelity_mng_list';
import FidelityExcelDownload from './FidelityExcelDownload';
import useModalStore from '../../../zustand/store/useModalStore';

import '../../../css/data-grid.css';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { Search16w } from '../../../images/icons/icons_component';

// 엑셀 다운로드 컬럼 너비 설정값
export const INFO_MNG_COL_WIDTHS = [7, 17, 17, 30, 30, 30, 30, 30, 30];

// 월별 정보관리 내역 진단 항목 설명
const extraColumnTexts = [
    '연령과 성별의 일치 여부',
    '* 내원 2일(48시간)이내 발병시간이 내원시간과 동일하게 전송된 경우',
    <>질병여부가 default '-' 인데 응급진료결과가 귀가(10), 전원(20),<br />
        입원(30), 사망(40)인 경우 오류
    </>,
    '* 질병여부에서 질병(1)과 질병 외(2) 입력률',
    <>
        응급진료결과가 전원(20), 입원(30), 사망(40)인데 질병여부가 진료외 방문(3)<br />
        으로 전송된 경우 오류(단, 주증상에 C0184701, C0582452, C0809949,<br />
        C0006110, C0004398 로 입력되어 있는 환자는 제외)
    </>,
    <>교통사고 손상 당사자는 입력되어 있으나 손상기전에는 교통사고(01, 03, 04, 08, 09)<br />가 전송되지 않은 경우
    </>,
    '전혀 착용 안 함, 비해당, 미상 중 하나로 체크되어 있는데 보호장구가 선택되어 있는지 여부',
    <>전원 보낸 의료기관 종류와 코드가 불일치하는 경우(예. 종류는 종합병원으로<br />
        표기되어 있는데 코드는 의원이 입력된 경우)
    </>,
    '* 응급증상 해당여부에서 응급(Y)과 비응급(N) 입력률',
    '내원 시 반응이 P 또는 U인데, 내원수단이 ‘도보(7)’인 경우',
    <>
        1. 응급진료결과 41번인데 내원시 반응이 A,V,P로 전송된경우<br />
        2. 응급진료결과 42,43번인데 내원시 반응이 A로 전송된경우
    </>,
    <>전원 보낼 의료기관 종류와 코드가 불일치하는 경우(예. 종류는 종합병원으로<br />
        표기되어 있는데 코드는 의원이 입력된 경우)
    </>,
    <>
        1. 응급진료결과는 전송되었는데, 퇴실일시가 미전송된 경우 오류<br />
        2. 퇴실일시-내원일시가 7일 이상일 경우 재검토
    </>,
    '입원후 결과는 전송되었는데, 퇴원일시가 미전송된 경우',
    '퇴원일시가 미전송 되었는데 입원 정보는 전송된 경우'
];

/**
 * @월별정보관리내역그리드
 * 월별정보관리내역 메뉴 / 월별 모니터링 메뉴 공용 그리드
 * 조회 API(fidelity_list.do, fidelity_mng_data.do)를 컴포넌트 내부에서 처리
    <InfoMngStatMonthlyGrid
        ref={mngGridRef}                // ref.search() 로 조회 실행
        searchParams={searchParams}     // 검색 조건(부모가 소유)
        showTitle={true}                // title 표출 여부(단독 메뉴는 main title 이 있으므로 false)
        onSearched={(result) => {}}     // 조회 완료 시 { totalItems, emogdesc, yyyymm } 전달
                                        // emogdesc / yyyymm 은 조회 시점 기준 값(엑셀 파일명 고정용)
    />
 * **/
export const InfoMngStatMonthlyGrid = forwardRef(({ searchParams, showTitle = true, onSearched }, ref) => {

    const [isSearched, setIsSearched] = useState(false);
    const [fidelityData, setFidelityData] = useState([{}]);
    const [totalItems, setTotalItems] = useState(0);

    const [bodylist, setBodyList] = useState([]);
    const head = ['진단항목', '정상저장률(%)', '재검토율(%)', '오류율(%)'];
    const except_body = ['PTMIINMN', 'PTMIEMRT'];
    const except_column = ['PTMIDGKD_2', 'PTMIEMSY'];

    const bodyData = async () => {
        try {
            const response = await axios.get('/ptmiapi/fidelity_list.do',
                { params: { listType: 'MANAGE' } }
            );

            setBodyList(response.data.fidelitylist);

        } catch (e) {
            console.error('body 내용 로딩 중 오류 발생', e);
        }
    }

    const getfidelityData = async () => {
        try {
            const response = await axios.get('/ptmiapi/fidelity_mng_data.do',
                { params: searchParams }
            );

            setFidelityData(generateFidelityMngList(bodylist, response.data, searchParams.searchType));
            setIsSearched(true);

            const cnt = response.data?.statInfo.TOTAL_CNT1 ?? 0;
            setTotalItems(cnt);

            onSearched?.({
                totalItems: cnt,
                emogdesc: response.data?.statInfo.EMOGDESC,
                yyyymm: searchParams.fromindt.substring(0, 6)
            });

        } catch (e) {
            console.error('월별 정보관리 내역 내용 로딩 중 오류 발생', e);
        }
    }

    useEffect(() => {
        bodyData();
    }, []);

    // 부모(단독 메뉴 / 모니터링) 조회 handle
    useImperativeHandle(ref, () => ({
        search: getfidelityData
    }));

    const onClickPopup = (e) => {
        const cls = e.currentTarget.dataset.cls;
        const type = e.target.dataset.type;
        const value = e.target.dataset.value;
        const clsdesc = e.target.dataset.clsdesc;

        const sendData = {
            cls: cls,
            type: type,
            clsdesc: clsdesc,
            flag: 'ptmi'
        }

        let popup = null;

        const totalObj = Object.assign({}, searchParams, sendData)

        if (isSearched && type && value !== ' ') {
            localStorage.setItem('sendData', JSON.stringify(totalObj));
            popup = window.open('/nedis/fidelity_2016/info_mng_stat_monthly_popup.do', '팝업', 'scrollbars=1, resizable=0, width=1100, height=565, top=30, left=30');
        }
    }

    const highlightText = (text) => {
        if (typeof text !== 'string') return text;

        // *로 시작하는 경우 스타일 적용
        if (text.trim().startsWith('*')) {
            return <span className='co_red'>{text}</span>;
        }

        return text;
    };

    const renderExtraColumn = (index) => {
        const content = extraColumnTexts[index];

        // JSX가 아닌 문자열만 강조 대상
        return highlightText(content);
    };

    // 조회 전에는 항목 목록(bodylist)만으로 빈 그리드를 표출
    const fidelityDataSet = fidelityData.length > 1 ? fidelityData : bodylist;

    // Dynamic Style Hook
    const { fontColor, hoverLightFontColor, borderColorGray } = useDynamicStyle();

    return (
        <>
            {showTitle &&
                <table>
                    <colgroup>
                        {Array.from({ length: 9 }).map((_, idx) => (
                            <col key={idx} width={`${100 / 9}%`} />
                        ))}
                    </colgroup>
                    <tbody>
                        <tr className='table_title'>
                            <td colSpan={9}>
                                <div className='search_btn_wrapper w-full'>
                                    <h5 className='sub_title m-0'>▶ NEDIS 월별 정보관리 내역 결과 (총&nbsp;<strong className={fontColor}>{totalItems}</strong>건)</h5>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            }
            <div className='data_grid_container'>
                <hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
                <div className={`data_list_table ${isSearched ? 'grid-animation' : ''}`}>
                    <table>
                        <colgroup>
                            <col width='4%' />
                            <col width='9%' />
                            <col width='9%' />
                            <col width='20%' />
                            <col width='20%' />
                            <col width='12%' />
                            <col width='7%' />
                            <col width='7%' />
                            <col width='12%' />
                        </colgroup>
                        <thead>
                            <tr className='table_header'>
                                {head.map((content, index) => (
                                    <th colSpan={index === 0 ? 5 : index === 2 ? 2 : 1} key={index}>
                                        {content}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {fidelityDataSet?.filter(content => !(searchParams.searchType === '02' && content.FIDELITY_TYPE === '02')).map((content, index) => {
                                const rowSpanSet = content.rowSpanValue ? content.rowSpanValue : ((content.FIDELITY_ID === 'PTMIDGKD_2' || content.FIDELITY_ID === 'PTMIEMSY') ? '2' : '1');
                                return (
                                    <React.Fragment key={index + 1}>
                                        <tr onClick={onClickPopup} className={searchParams.searchType === '02' && content.FIDELITY_TYPE === '02' ? 'hidden' : content.FIDELITY_TYPE === '02' ? '' : ' colored'} data-cls={content.SEQ < 10 ? '0' + (content.SEQ) : content.SEQ}>
                                            <td rowSpan={rowSpanSet}>{index + 1}</td>
                                            {content.FIDELITY_ID === 'PTMIINMN' ? <td className='middle' rowSpan='2'><span>내원 시<br /> 활력징후</span></td> : ''}
                                            {content.FIDELITY_ID?.includes('PTMIDGKD_') ? '' : <td rowSpan={content.FIDELITY_ID === 'PTMIDGKD' ? '4' : content.FIDELITY_ID === 'PTMIEMSY' ? '2' : '1'} colSpan={except_body.includes(content.FIDELITY_ID) ? '1' : '2'}>{content.FIDELITY_NAME}</td>}
                                            <td colSpan={2} rowSpan={rowSpanSet} className='text-left'>{renderExtraColumn(index)}</td>
                                            <td rowSpan={rowSpanSet} id={content.FIDELITY_ID} data-value={isSearched ? except_column.includes(content.FIDELITY_ID) ? ' ' : content.ORG_NORMAL : ''}> {isSearched ? except_column.includes(content.FIDELITY_ID) ? ' ' : content.ORG_NORMAL : '0.00'}</td>
                                            {(content.rowSpanValue === 2 || (content.FIDELITY_ID === 'PTMIDGKD_2' || content.FIDELITY_ID === 'PTMIEMSY')) ? (
                                                <>
                                                    <td className='h-8 py-1 bold'>{content.FIDELITY_ID === 'PTMIDGKD_2' ? '질병' : '응급증상율'}</td>
                                                    <td className='h-8 py-1 bold'>{content.FIDELITY_ID === 'PTMIDGKD_2' ? '질병외' : '비응급증상율'}</td>
                                                </>
                                            ) : (
                                                <td colSpan={2} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''} data-type='01' data-clsdesc={content.FIDELITY_NAME} data-value={isSearched ? content.ORG_REVIEW : ''}>
                                                    {isSearched ? content.ORG_REVIEW : '0.00'}
                                                </td>
                                            )}
                                            <td rowSpan={rowSpanSet} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''} data-type='02' data-clsdesc={content.FIDELITY_NAME} data-value={isSearched ? except_column.includes(content.FIDELITY_ID) ? ' ' : content.ORG_ERROR : ''}>
                                                {isSearched ? except_column.includes(content.FIDELITY_ID) ? ' ' : content.ORG_ERROR : '0.00'}
                                            </td>
                                        </tr>
                                        {(content.rowSpanValue === 2 || (content.FIDELITY_ID === 'PTMIDGKD_2' || content.FIDELITY_ID === 'PTMIEMSY')) && (
                                            <tr onClick={onClickPopup} className={searchParams.searchType === '02' && content.FIDELITY_TYPE === '02' ? 'hidden' : content.FIDELITY_TYPE === '02' ? '' : ' colored'} data-cls={content.SEQ < 10 ? '0' + (content.SEQ) : content.SEQ}>
                                                <td className={`h-8 py-1 sub_table ${isSearched && 'underline-pointer ' + hoverLightFontColor}`} data-type='03' data-value={isSearched ? content.ORG_REVIEW_1 : '0.0'} data-clsdesc={content.FIDELITY_NAME + (content.FIDELITY_ID === 'PTMIDGKD_2' ? '(질병)' : '(응급증상율)')}>{isSearched ? content.ORG_REVIEW_1 : '0.0'}</td>
                                                <td className={`h-8 py-1 sub_table ${isSearched && 'underline-pointer ' + hoverLightFontColor}`} data-type='04' data-value={isSearched ? content.ORG_REVIEW_2 : '0.0'} data-clsdesc={content.FIDELITY_NAME + (content.FIDELITY_ID === 'PTMIDGKD_2' ? '(질병외)' : '(비응급증상율)')}>{isSearched ? content.ORG_REVIEW_2 : '0.0'}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
                <hr className='mr-1-1 co_border_gray' />
                {/* 범례 - 엑셀 다운로드에 포함되도록 table 로 구성 */}
                <table>
                    <colgroup>
                        {Array.from({ length: 9 }).map((_, idx) => (
                            <col key={idx} width={`${100 / 9}%`} />
                        ))}
                    </colgroup>
                    <tbody>
                        <tr>
                            <td colSpan={3} className='border-none py-0'>
                                <div className='flex mt-2 items-center'>
                                    <span className='st_row bold'>■ </span>
                                    <p className='ml-1'>&nbsp;센터급, 기관급 공통항목&nbsp;</p>
                                    <span className='co_red'>&nbsp;* 재검토 항목</span>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={9} className='border-none pt-0'></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    )
});

// 모니터링에는 사용하지 않고 단독 메뉴에서만 표출하는 비고 (엑셀 다운로드에 그대로 포함되도록 table 로 구성하고 tableRef 안에 배치)
const INFO_MNG_DESC_TEXTS = [
    '· 정상저장률 : 항목 간 전후관계가 일관되게 입력됨을 의미',
    '· 재검토율 : 오류내역은 아니지만 확인이 필요한 경우를 의미',
    '· 오류율 : 항목 간 전후관계가 부합되거나 자료 입력에 규칙성(고정 값)이 존재하는 경우를 의미',
    '· 퇴원정보의 미입력률에는 재원중인 환자도 포함될 수 있음.'
];

export const InfoMngStatMonthlyDesc = () => (
    <table>
        <colgroup>
            {Array.from({ length: 9 }).map((_, idx) => (
                <col key={idx} width={`${100 / 9}%`} />
            ))}
        </colgroup>
        <tbody>
            {INFO_MNG_DESC_TEXTS.map((content, index) => (
                <tr key={index}>
                    {/* excel_black_text : 엑셀전용 클래스 */}
                    <td colSpan={9} className='border-none pt-0 excel_black_text'>
                        <p className='ml-2'>{content}</p>
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const Info_mng_stat_monthly = () => {

    const today = new Date();
    const currentYear = today.getFullYear();
    let currentMonth = today.getMonth() + 1;
    if (currentMonth < 10) {
        currentMonth = '0' + currentMonth;
    }

    const [searchParams, setSearchParams] = useState({
        searchYear: currentYear,
        searchMonth: currentMonth,
        searchEmog: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('useremcd') : '',
        searchGrdc: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('emoggrdc2') : '',
        searchLoca: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('emogloca') : '',
        searchType: '01',
        fromindt: currentYear + '' + currentMonth + '01',
        toindt: currentYear + '' + currentMonth + '31',
    });

    const [isSearched, setIsSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // 그리드 조회 결과 (건수)
    const [totalItems, setTotalItems] = useState(0);
    
    // 엑셀 파일명을 '조회 시점' 기준으로 고정하기 위한 state
    const [excelInfo, setExcelInfo] = useState({ emogdesc: '', yyyymm: '' });
    // 그리드 조회 실행용 ref / 엑셀 다운로드 대상 ref
    const gridRef = useRef(null);
    const tableRef = useRef(null);

    // toast modal open function
    const showNotiModal = useModalStore(state => state.showNotiModal);

    const search = async () => {
        if (searchParams.searchEmog === '') {
            showNotiModal('먼저 해당 응급의료기관을 선택하십시오.');
        } else {
            setIsLoading(true);
            await gridRef.current?.search();
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newParams = {
            ...searchParams,
            [name]: value
        };
        if (name === 'searchYear' || name === 'searchMonth') {
            const year = name === 'searchYear' ? value : searchParams.searchYear;
            const month = name === 'searchMonth' ? value : searchParams.searchMonth;
            newParams.fromindt = `${year}${month}01`;
            newParams.toindt = `${year}${month}31`;
        }
        setSearchParams(newParams);
    };

    // Dynamic Style Hook
    const { fontColor, primaryBackColor } = useDynamicStyle();

    return (
        <Main>
            <div className='com-search'>
                <table>
                    <colgroup>
                        <col width='10%' />
                        <col width='20%' />
                        <col width='10%' />
                        <col width='20%' />
                        <col width='10%' />
                        <col width='20%' />
                    </colgroup>
                    <tbody>
                        <tr>
                            <NewCommonSelect
                                hospitalTF={true}
                                searchParams={searchParams}
                                handleInputChange={handleInputChange}
                                exValidation={true}
                            />
                        </tr>
                        <tr>
                            <th>내원기간</th>
                            <td>
                                <select id='searchYear'
                                    name='searchYear'
                                    value={searchParams.searchYear}
                                    onChange={handleInputChange}
                                >
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = (currentYear - i).toString()
                                        return (
                                            <option key={year} value={year}>
                                                {year} 년
                                            </option>
                                        );
                                    })}
                                </select>
                                &ensp;
                                <select id='searchMonth'
                                    name='searchMonth'
                                    value={searchParams.searchMonth}
                                    onChange={handleInputChange}
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = (i + 1).toString().padStart(2, '0');
                                        return (
                                            <option key={month} value={month}>
                                                {month} 월
                                            </option>
                                        );
                                    })}
                                </select>
                            </td>
                            <th>기관구분</th>
                            <td colSpan='3'>
                                <select id='searchType'
                                    name='searchType'
                                    value={searchParams.searchType}
                                    onChange={handleInputChange}
                                >
                                    <option value='01'>센터형</option>
                                    <option value='02'>기관형</option>
                                </select>
                                <span className='co_red ml-2'>* 표출되는 항목에 영향을 줍니다.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className='search_btn_wrapper'>
                <span className='total_items'>전체&nbsp;<strong className={fontColor}>{totalItems}</strong>건</span>
                <div className='btn_container'>
                    <button onClick={search} disabled={isLoading ? 'disabled' : ''} className={`search_btn ${primaryBackColor}`}><Search16w />검색</button>
                    <FidelityExcelDownload
                        targetRef={tableRef}
                        disabled={!(isSearched && !isLoading)}
                        excelFileName={`충실도_월별정보관리내역_${excelInfo.emogdesc}_${excelInfo.yyyymm}(${searchParams.searchType === '01' ? '센터형' : '기관형'})`}
                        colWidths={INFO_MNG_COL_WIDTHS}
                    />
                </div>
            </div>
            {isLoading && <Loading />}
            <div ref={tableRef} className={`flex flex-col flex-grow min-h-0 ${isLoading ? 'hidden' : ''}`}>
                <InfoMngStatMonthlyGrid
                    ref={gridRef}
                    searchParams={searchParams}
                    showTitle={false}
                    onSearched={(result) => {
                        setIsSearched(true);
                        setTotalItems(result.totalItems);
                        setExcelInfo({ emogdesc: result.emogdesc, yyyymm: result.yyyymm });
                    }}
                />
                <InfoMngStatMonthlyDesc />
            </div>
        </Main>
    )
}

export default Info_mng_stat_monthly;
